import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Image } from '../components/apartment/header/header.component';
import gsap from 'gsap';

@Injectable({
    providedIn: 'root',
})
export class DOMHandlerService {
    isMobile = window.innerWidth < 768;
    isDesktop = window.innerWidth >= 1024;
    intlDateFormatGerman = new Intl.DateTimeFormat('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    intlDateFormatEnglish = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    constructor(private sanitizer: DomSanitizer) {}

    toSafeHTML(html: string) {
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    getSmallestImage(image: Image | undefined): string {
        if (!image) {
            return '';
        }
        if (image.formats?.thumbnail) {
            return image.formats.thumbnail.url;
        }
        if (image.formats?.small) {
            return image.formats.small.url;
        }
        if (image.formats?.medium) {
            return image.formats.medium.url;
        }
        if (image.formats?.large) {
            return image.formats.large.url;
        }
        return image.url;
    }

    getSmallestImageAsWebp(image: Image | undefined): string {
        if (!image) {
            return '';
        }
        if (image.formats?.thumbnail) {
            return image.formats.thumbnail.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        if (image.formats?.small) {
            return image.formats.small.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        if (image.formats?.medium) {
            return image.formats.medium.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        if (image.formats?.large) {
            return image.formats.large.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        return image.url.replace('upload', 'upload/t_to-webp');
    }

    getLargestImage(image: Image | undefined): string {
        if (!image) {
            return '';
        }
        if (image.formats?.large) {
            return image.formats.large.url;
        }
        if (image.formats?.medium) {
            return image.formats.medium.url;
        }
        if (image.formats?.small) {
            return image.formats.small.url;
        }
        if (image.formats?.thumbnail) {
            return image.formats.thumbnail.url;
        }
        return image.url;
    }

    getLargestImageAsWebp(image: Image | undefined): string {
        if (!image) {
            return '';
        }
        if (image.formats?.large) {
            return image.formats.large.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        if (image.formats?.medium) {
            return image.formats.medium.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        if (image.formats?.small) {
            return image.formats.small.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        if (image.formats?.thumbnail) {
            return image.formats.thumbnail.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        return image.url.replace('upload', 'upload/t_to-webp');
    }

    generateSrcset(image: Image | undefined): string {
        if (!image) {
            return '';
        }
        let srcset = '';
        if (image.formats?.large) {
            srcset = `${image.formats.large.url} ${image.formats.large.width}w, `;
        }
        if (image.formats?.medium) {
            srcset += `${image.formats.medium.url} ${image.formats.medium.width}w, `;
        }
        if (image.formats?.small) {
            srcset += `${image.formats.small.url} ${image.formats.small.width}w, `;
        }
        if (image.formats?.thumbnail) {
            srcset += `${image.formats.thumbnail.url} ${image.formats.thumbnail.width}w, `;
        }
        return srcset;
    }
    generateSrcsetAsWebp(image: Image | undefined): string {
        if (!image) {
            return '';
        }
        let srcset = '';
        if (image.formats?.large) {
            srcset = `${image.formats.large.url.replace('upload', 'upload/t_to-webp')} ${image.formats.large.width}w, `;
        }
        if (image.formats?.medium) {
            srcset += `${image.formats.medium.url.replace('upload', 'upload/t_to-webp')} ${image.formats.medium.width}w, `;
        }
        if (image.formats?.small) {
            srcset += `${image.formats.small.url.replace('upload', 'upload/t_to-webp')} ${image.formats.small.width}w, `;
        }
        if (image.formats?.thumbnail) {
            srcset += `${image.formats.thumbnail.url.replace('upload', 'upload/t_to-webp')} ${image.formats.thumbnail.width}w, `;
        }
        return srcset;
    }

    spawnAddToCartAnimation(event: MouseEvent | undefined, amount: number) {
        if (!event) {
            return;
        }
        const mousePosition = this.getMousePosition(event);
        const animatedDiv = document.createElement('div');
        animatedDiv.classList.add('addToCartItem');
        animatedDiv.style.left = `${mousePosition.x}px`;
        animatedDiv.style.top = `${mousePosition.y}px`;
        animatedDiv.innerHTML = `+${amount}`;
        document.body.appendChild(animatedDiv);

        const targetElement = this.isDesktop
            ? document.getElementById('cart-item-amount-desktop')
            : document.getElementById('cart-item-amount-mobile');

        if (targetElement) {
            const targetRect = targetElement.getBoundingClientRect();

            gsap.to(animatedDiv, {
                x: targetRect.left - mousePosition.x,
                y: targetRect.top - mousePosition.y,
                scale: 0.5,
                opacity: 0,
                duration: 1,
                ease: 'power3.in',
                onComplete: () => {
                    document.body.removeChild(animatedDiv);
                },
            });
        } else {
            setTimeout(() => {
                document.body.removeChild(animatedDiv);
            }, 1000);
        }
    }

    formatDate(value: string, languageCode: string): string {
        if (languageCode === 'en') {
            return this.intlDateFormatEnglish.format(new Date(value));
        }
        return this.intlDateFormatGerman.format(new Date(value));
    }

    closeCollapse(id: string) {
        const element = document.getElementById(id);
        if (!element) {
            return;
        }
        //@ts-ignore
        window?.HSCollapse?.hide(element);
    }

    addStructuredData(data: any) {
        const existingScript = document.querySelector(
            'script[type="application/ld+json"]',
        );
        if (existingScript) {
            document.head.removeChild(existingScript);
        }

        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
    }

    private getMousePosition(event: MouseEvent) {
        return {
            x: event.clientX,
            y: event.clientY,
        };
    }
}
